import { SEO } from '@/components/common/SEO'
import { FatFooterLayout } from '@/layouts/FatFooterLayout'
import { NextPage } from 'next'
import home_page_img from '@/public/images/site-preview.gif'
import { RoundedContainer } from '@/components/containers/RoundedContainer'
import { PanelsArray } from '@/components/cards/PanelsArray'
import { WingCircle } from '@/components/icons/WingCircle'
import { CrossIcon } from '@/components/icons/CrossIcon'
import Image from 'next/image'
import science_coaching_icon from '@/public/images/science_coaching_icon.png'
import { imageLoader } from '../loaders'
import { Button } from '@/components/controls/Button'
import { CustomTrustpilot } from '@/components/common/CustomTrustpilot'
import { SimpleCarouselItem } from '@/components/carousel/SimpleCarousel'
import { TestimonialBig } from '@/components/common/TestimonialBig'
import { SimpleCarousel } from '@/components/carousel/SimpleCarousel'
import { Accordion } from '@/components/blocks/Accordion'
import { useLazyLottieAnimations } from '@/hooks/LazyLottieAnimations'
import { createDynamicAnimationWithRef } from '@/utils/animation'
import { MainHero } from '@/components/heros/MainHero'
import { Carousel3d } from '@/components/carousel-3d/Carousel3d'
import { useBetterMediaQuery } from '@/hooks/BetterMediaQuery'

import nitzan_ava from '@/public/images/nitzan_ava.jpg'
import sean_ava from '@/public/images/sean_ava.jpg'
import danielle_ava from '@/public/images/danielle_ava.jpg'
import julie_ava from '@/public/images/julie_ava.jpg'
import mark_ava from '@/public/images/mark_ava.jpg'
import yoav_ava from '@/public/images/yoav_ava.jpg'
import stephane_ava from '@/public/images/stephane_ava.jpg'
import calendars from '@/public/images/calendars.png'


import testim_ava1 from '@/public/images/testim_ava1.jpg'
import testim_ava2 from '@/public/images/testim_ava2.jpg'
import testim_ava3 from '@/public/images/XayLi_Barclay.jpeg'
import testim_ava4 from '@/public/images/testim_ava12.jpg'
import { H1 } from '@/components/headlines/H1'
import { P } from '@/components/common/P'
import { useElementWidth } from '@/hooks/ElementWidth'
import { H2 } from '@/components/headlines/H2'
import { H3 } from '@/components/headlines/H3'
import { RecommendedIn } from '@/components/common/RecommendedIn'
import { useQuery } from '@/utils/url'
import Link from 'next/link'
import { Container } from '@/components/common/Container'
import { accountabilityCoachingFAQs } from 'data/faqs'

const DynamicAnimationWithRef = createDynamicAnimationWithRef(
  () => import('@/components/dynamic/DynamicAnimation').then((mod) => mod.DynamicAnimation)
)

const afterCoachingItems = [
  {
    icon: <WingCircle />,
    title: 'Crystal clear direction',
    description: 'Know exactly what to work on and when you\'ve done enough.'
  },
  {
    icon: <WingCircle />,
    title: 'High-impact action',
    description: 'Moving faster in months than you have in years.'
  },
  {
    icon: <WingCircle />,
    title: 'Confident decisions',
    description: 'Overcome fears and take bold action on your best ideas.'
  },
  {
    icon: <WingCircle />,
    title: 'Organized & in control',
    description: 'Mental clutter cleared, important work never slips through cracks.'
  },
  {
    icon: <WingCircle />,
    title: 'Sustainable motivation',
    description: 'Lasting drive on autopilot, always finish what you start.'
  },
  {
    icon: <WingCircle />,
    title: 'True work-life balance',
    description: 'Working smarter with clear boundaries between work and home.'
  },
]

const beforeCoachingItems = [
  {
    icon: <CrossIcon />,
    title: 'Drowning in chaos',
    description: 'Overwhelmed by endless to-do lists and urgent tasks.'
  },
  {
    icon: <CrossIcon />,
    title: 'Analysis paralysis',
    description: 'Best ideas trapped in your head, afraid to take action.'
  },
  {
    icon: <CrossIcon />,
    title: 'Spinning wheels',
    description: 'Same vision for years with no meaningful progress.'
  },
  {
    icon: <CrossIcon />,
    title: 'Burned out',
    description: 'Fighting fires all day, can\'t switch off at night.'
  },
  {
    icon: <CrossIcon />,
    title: 'Flying solo',
    description: 'Isolated and lacking accountability structure.'
  },
  {
    icon: <CrossIcon />,
    title: 'Business bottleneck',
    description: 'Working IN your business, not ON it.'
  },
]

const willExperienceItems = [
  {
    icon: <WingCircle />,
    title: 'Deep accountability and pattern recognition',
    description: 'Your coach tracks your progress over time and helps you break through obstacles that keep showing up.'
  },
  {
    icon: <WingCircle />,
    title: 'Strategic weekly focus',
    description: 'Stop spinning your wheels on busy work and consistently tackle the projects that will actually grow your business.'
  },
  {
    icon: <WingCircle />,
    title: 'Clear, executable missions',
    description: 'Turn overwhelming goals into specific weekly quests that feel achievable and motivating.'
  },
  {
    icon: <WingCircle />,
    title: 'Reliable follow-through habits',
    description: 'Finally become someone who consistently does what they say they\'re going to do.'
  },
  {
    icon: <WingCircle />,
    title: 'Professional thinking partner',
    description: 'Get help you work through challenges and refine your approach.'
  },
  {
    icon: <WingCircle />,
    title: 'Visible weekly progress',
    description: 'Build real momentum as you complete meaningful work week after week.'
  },
]

const membershipItems = [
  {
    icon: <WingCircle />,
    title: 'Dedicated Coach',
    description: 'Work with a highly trained, full time specialist accountability coach with over 10,000 hours of experience applying our methodology in hundreds of industries.'
  },
  {
    icon: <WingCircle />,
    title: 'Weekly 1-to-1 Calls',
    description: 'Weekly 30 minute coaching sessions over the phone provide the productivity ritual that requires zero willpower. Simply answer the call, and by the end you’ll have a fine-turned action plan for the next 7 days.'
  },
  {
    icon: <WingCircle />,
    title: 'Unlimited Text & Email',
    description: 'Drop ideas, todos, or talk through roadblocks with your coach whenever you need with unlimited text and email support. It’s like having a personal productivity concierge.'
  },
  {
    icon: <WingCircle />,
    title: 'Video Training Library',
    description: 'Alongside your coaching you also get access to our huge library of videos on the psychology of entrepreneurship to help you navigate specific roadblocks from imposter syndrome to burnout. Plus new live sessions and recordings added every month.'
  },
  {
    icon: <WingCircle />,
    title: '30 Day Guarantee',
    description: 'We’re so confident that you’ll love this method, our coaches, and the results you get, that we offer a full refund within your first month of coaching for any reason. Either it works for you, or you get your money back. No questions asked, no hard feelings, just email us at info@commitaction.com'
  },
  {
    component: (
      <div className='bg-green-gradient p-[40px] xl:m-[8px] rounded-[16px] w-full flex flex-col justify-center'>
        <div className='flex flex-col items-center justify-center mb-[24px]'>
          <div className='flex items-center ml-[10px] text-white gap-[6px]'>
            <span className='text-[48px] xl:text-[70px] font-bold leading-[1]'>$575</span>
            <span className='text-[16px] xl:text-[22px] leading-[1]'><span className='ml-[12px]'>per</span> <br/> / month</span>
          </div>
          <p className="text-white/60">*or $460 / month billed annually</p>
        </div>
        <div className='flex justify-center'>
          <Button fullWidth={true} type="white" fontSize={18} height="lg" textCenter={true} href={'/checkout/accountability-coaching'} disableArrow={true} chevron={false} maxWidth={280}>Get Started Now</Button>
        </div>
      </div>
    )
  }
]

const AccountabilityCoach: NextPage = () => {
  const sm = useBetterMediaQuery('(min-width: 768px)')
  const lg = useBetterMediaQuery('(min-width: 1024px)')
  const heroWidth = useElementWidth('hero-container', 100)
  const whyStartNowWidth = useElementWidth('why-start-now-carousel', 480)
  const { lottieRefs, rowRefs, animsToLoad } = useLazyLottieAnimations(3)

  const applyWidth = () => {
    if (!sm) {
      return heroWidth || 350
    }

    if (sm && !lg) {
      return 280
    }

    if (sm) {
      return 320
    }
  }

  return (
    <>
      <SEO
        title="Accountability Coaching for Entrepreneurs"
        description="Double your focus & effectiveness overnight with practical science-backed coaching."
        keywords='Accountability Coaching for Entrepreneurs'
        canonicalUrl={process.env.NEXT_PUBLIC_SITE + '/accountability-coaching/'}
        ogImageUrl={`${process.env.NEXT_PUBLIC_SITE}${home_page_img.src}`}
      />
      <div className='font--accent'>
        <MainHero>
          <div className='flex flex-col md:flex-row items-center justify-between pt-[80px] xl:pt-[100px] xl:pb-[100px]'>
            <div className='text-center lg:text-left max-w-[520px] text-[18px] mb-[60px] xl:mb-0'>
              <H1 color='white'>Accountability Coaching for Entrepreneurs</H1>
              <P className='mb-[16px] font-bold !opacity-[1]'>Double your focus & effectiveness overnight with practical science-backed coaching.</P>
              <P className='mb-[16px]'>Get clarity on your most important work and the accountability to make sure it gets done!</P>
              <P>Stop self-sabotaging, lock in laser focus, and get more done in a few weeks than other entrepreneurs do in months!</P>
              <div className='mt-[40px]'>
                <Button fullWidth={true} type="green" height="lg" fontSize={18} textCenter={true} href={useQuery('/checkout/accountability-coaching')} disableArrow={true} chevron={false} maxWidth={320}>Sign Up Now</Button>
              </div>
            </div>
            <div className='relative w-full md:w-[48%] flex flex-col items-center justify-center xl:mt-[-40px]'>
              <div className='flex mb-[25px] justify-center'>
                <CustomTrustpilot stars={true} large={!sm ? false : true} vertical={!sm ? false : true}></CustomTrustpilot>
              </div>
              {sm && <Carousel3d
                count={4}
                width={330}
                height={260}
                inverseScaling={300}
                display={3}
                perspective={10}
                bullets={true}
                divisor={sm && !lg ? 2.8 : 1.8}
                startIndex={0}
                animationSpeed={600}
                controlsVisible={true}
                className="carousel-3d-container--sec"
                nextHtml={<span className='carousel_3d_next_control--sec'></span>}
                prevHtml={<span className='carousel_3d_prev_control--sec'></span>}
                slides={[
                  {
                    content: <TestimonialBig
                      name="Skye K"
                      className="testimonial-hero-anim-less"
                      text="In the 3 months I’ve been with Commit Action my revenue has increased by 50% and I’ve hired a team to help me execute which has bought my total hours worked down to 25 per week (while growing 2 companies)"
                      avatarUrl={testim_ava1.src}
                      caption="Founder | Profit Partnerships Group"
                      height={255}
                    ></TestimonialBig>
                  },
                  {
                    content: <TestimonialBig
                      name="Steve Krakower"
                      className="testimonial-hero-anim-less"
                      text="The results speak for themselves — in the few months that I have been working working with Commit Action, our revenue has increased 25% and we finally hit our first 6-figure month"
                      avatarUrl={testim_ava2.src}
                      caption="Founder | Harbor Marketing Agency"
                      height={255}
                    ></TestimonialBig>
                  },
                  {
                    content: <TestimonialBig
                      name="XayLi Barclay"
                      className="testimonial-hero-anim-less"
                      text="Since signing up for Commit Action, I've been able to blow through my initial goals and create even bigger ones. Meeting with my coach Nick is always one of the highlights of my week!"
                      avatarUrl={testim_ava3.src}
                      caption="Founder &amp; CEO | TDG Visual Solutions"
                      height={255}
                    ></TestimonialBig>
                  },
                  {
                    content: <TestimonialBig
                      name="Khierstyn Ross"
                      className="testimonial-hero-anim-less"
                      text="As an entrepreneur, I constantly feel overwhelmed and second guess if what I'm working on will move me toward my goals, with Commit Action's focus, now I don't feel that way as I'm working"
                      avatarUrl={testim_ava4.src}
                      caption="Founder &amp; CEO | Launch &amp; Scale™"
                      height={255}
                    ></TestimonialBig>
                  }
                ]}
              />}
            </div>
          </div>
          {!sm &&
            <div className='mb-[80px]'>
              <SimpleCarousel
                loop={true}
                width={applyWidth()}
                autoplay={false}
                animationSpeed={10000}
                gap={16}
                controlsVisible={sm ? true : false}
                endLess={true}
                bullets={true}
                overflow={'visible'}
                containerClass="checkout-testimonials-carousel2"
                nextHtml={<span className='checkout-testimonials-carousel_next_control home-page-carousel_next_control'></span>}
                prevHtml={<span className='checkout-testimonials-carousel_prev_control home-page-carousel_prev_control'></span>}
              >
                <SimpleCarouselItem>
                  <TestimonialBig
                    name="Skye K"
                    className="testimonial-hero-anim-less"
                    text="“In the 3 months I’ve been with Commit Action my revenue has increased by 50% and I’ve hired a team to help me execute which has bought my total hours worked down to 25 per week (while growing 2 companies)”"
                    avatarUrl={testim_ava1.src}
                    caption="Founder | Profit Partnerships Group"
                    height={sm ? 280 : 255}
                  ></TestimonialBig>
                </SimpleCarouselItem>
                <SimpleCarouselItem>
                  <TestimonialBig
                    name="Steve Krakower"
                    className="testimonial-hero-anim-less"
                    text="“The results speak for themselves — in the few months that I have been working working with Commit Action, our revenue has increased 25% and we finally hit our first 6-figure month”"
                    avatarUrl={testim_ava2.src}
                    caption="Founder &amp; CEO | Harbor Marketing Agency"
                    height={sm ? 280 : 255}
                  ></TestimonialBig>
                </SimpleCarouselItem>
                <SimpleCarouselItem>
                  <TestimonialBig
                    name="XayLi Barclay"
                    className="testimonial-hero-anim-less"
                    text="“Since signing up for Commit Action, I've been able to blow through my initial goals and create even bigger ones. Meeting with my coach Nick is always one of the highlights of my week!”"
                    avatarUrl={testim_ava3.src}
                    caption="Founder &amp; CEO | TDG Visual Solutions"
                    height={sm ? 280 : 255}
                  ></TestimonialBig>
                </SimpleCarouselItem>
                <SimpleCarouselItem>
                  <TestimonialBig
                    name="Khierstyn Ross"
                    className="testimonial-hero-anim-less"
                    text="“As an entrepreneur, I constantly feel overwhelmed and second guess if what I'm working on will move me toward my goals, with Commit Action's focus, now I don't feel that way as I'm working”"
                    avatarUrl={testim_ava4.src}
                    caption="Founder &amp; CEO | Launch &amp; Scale™"
                    height={sm ? 280 : 255}
                  ></TestimonialBig>
                </SimpleCarouselItem>
              </SimpleCarousel>
            </div>}
          <RecommendedIn />
        </MainHero>
        <RoundedContainer className='py-[64px] xl:py-[140px]'>
          <div className='container container-1200'>
            <div className='flex flex-col md:flex-row mb-[80px]'>
              <div className='max-w-[500px]'>
                <H2>Why Choose Accountability Coaching?</H2>
              </div>
              <div className='text-[18px] text-black/70 max-w-[500px]'>
                <P className='mb-[16px]'>If you want to take consistent action and reach your full potential in business then you <b className='text-black'>need</b> structure and accountability. Because without clear goals, timelines and support, it’s impossible to be truly effective.</P>
                <P>Entrepreneurs use our coaching to get unstuck, work smarter, and build successful businesses like clockwork.</P>
              </div>
            </div>
            <PanelsArray className='mb-[80px]' iconPosition='left' title="Before Accountability Coaching" items={beforeCoachingItems} />
            <PanelsArray className='mb-[80px] xl:mb-[160px]' iconPosition='left' title="After Accountability Coaching" items={afterCoachingItems} />
          </div>
          <section className='bg-white py-[80px] xl:py-[140px]'>
            <div className='container container-1200'>
              <div className='max-w-[600px] mb-[40px] xl:mb-[80px]'>
                <H2>Science-Backed Practical Coaching</H2>
              </div>
              <div className='flex flex-col md:flex-row text-black/70 justify-between'>
                <div className='max-w-[500px] text-[18px]'>
                  <P className='mb-[16px]'>Our coaching methodology is developed with researchers at Harvard & NYU. So it&apos;s grounded in decades of behavioral science research, not wishful thinking or abstract platitudes.</P>
                  <P>While many coaches offer vague motivational talks, we focus exclusively on what the research proves actually works: <b className='text-black'>specificity, measurement, deadlines, and structured accountability.</b></P>
                  <div className='w-[240px] mt-[30px]'>
                    <Image src={science_coaching_icon} alt="Accountability Coach" loader={imageLoader} />
                  </div>
                </div>
                <div className='max-w-[500px] text-[18px] mt-[-20px] xl:mt-0'>
                  <h6 className='font-bold mb-[16px] text-black'>How it works:</h6>
                  <P className='mb-[40px]'>We help you clarify your biggest goals and highest-leverage opportunities. Then we apply the science of productivity to create concrete, actionable plans. Finally, we hold you accountable to your own commitments every single week.</P>
                  <h6 className='font-bold mb-[16px] text-black'>The result:</h6>
                  <P>You&apos;ll move faster and achieve more than you ever have before, without sacrificing any of the freedom and control that drew you to entrepreneurship in the first place.</P>
                </div>
              </div>
            </div>
          </section>
          <RoundedContainer style='dark' className='py-[80px] xl:py-[140px]' doubleRounded>
            <div className='container container-1200'>
              <div className='xl:text-center max-w-[800px] mx-auto mb-[80px]'>
                <H2 color='white'>How Our <br className='block xl:hidden'/>Coaching Works</H2>
                <P className='text-white mb-[16px] font-bold opacity-[1]'>Every week, your coach leads you through a 30-minute planning ritual over the phone.</P>
                <P className='text-white'>No will power, no learning curve. Just show up, think strategically, and walk away with a focused plan that saves hours and drives growth.</P>
              </div>
              <div className='text-white/60 text-[18px]'>
                <div className='flex flex-col xl:flex-row mb-[80px] justify-between items-center'>
                  <div className="md:w-[40%] shrink-0 mb-[80px] xl:mb-0">
                    <H3 color='white'>Step 1: Review</H3>
                    <P className='text-white mb-[16px]'>Your coach helps you zoom out and reflect on what’s working and where you’re getting stuck. Together you’ll unpack your week and bank powerful insights.</P>
                    <P className='text-white'>Expect sharp questions, smart pattern recognition, and clarity on what’s holding you back (like overcommitting or avoiding key projects).</P>
                  </div>
                  <div className="md:w-[40%]" ref={rowRefs.current[1]}>
                    { animsToLoad[1] && <DynamicAnimationWithRef ref={lottieRefs.current[1]} name="how-it-works-2" /> }
                  </div>
                </div>
                <div className='flex flex-col xl:flex-row mb-[80px] justify-between items-center'>
                  <div className="md:w-[40%] order-1 xl:order-2 shrink-0 mb-[80px] xl:mb-0">
                    <H3 color='white'>Step 2: Clarify</H3>
                    <P className='text-white mb-[16px]'>Next, you’ll cut through the noise and use 80/20 thinking to identify the small number of high-impact tasks that truly produce results.</P>
                    <P className='text-white'>You’ll connect weekly work to long-term goals, so every action feels like the next step on a direct path to your dream business.</P>
                  </div>
                  <div className="md:w-[40%] order-2 xl:order-1" ref={rowRefs.current[3]}>
                    <Image src={calendars} alt="Accountability Coach" loader={imageLoader} />
                  </div>
                </div>
                <div className='flex flex-col xl:flex-row mb-[80px] justify-between items-center'>
                  <div className="md:w-[40%] shrink-0 mb-[80px] xl:mb-0">
                    <H3 color='white'>Step 3: Commit</H3>
                    <P className='text-white mb-[16px]'>Now it’s execution time. Your coach turns those priorities into a focused weekly plan, built on proven productivity science. </P>
                    <P className='text-white'>You’ll walk away with a detailed action plan for your key projects for the week. Specific achievable commitments for the week, all time-blocked and synced to your calendar.</P>
                  </div>
                  <div className="md:w-[40%]" ref={rowRefs.current[2]}>
                    { animsToLoad[2] && <DynamicAnimationWithRef ref={lottieRefs.current[2]} name="how-it-works-3" /> }
                  </div>
                </div>
              </div>
              <div className='xl:text-center max-w-[800px] mx-auto mb-[80px]'>
                <H2 color='white'>What Happens Next</H2>
                <P className='text-white'>This isn’t a one-off productivity hack, it’s the ultimate “meta-habit” for identifying your biggest opportunities and taking decisive action, on autopilot.</P>
              </div>
              <PanelsArray className='mb-[100px] xl:mb-[160px] mb-[80px]' style='light' height='xl:h-[300px]' title="Here’s what you’ll experience" items={willExperienceItems} />
              <div className='flex flex-col xl:flex-row xl:items-center'>
                <div className='max-w-[590px] mb-[24px] xl:mb-0'>
                  <H2 color='white'>Bottom line</H2>
                  <P className='text-white'>With structure, strategy, and expert support — you finally become the kind of entrepreneur who hits home run after home run.</P>
                </div>
                <div className='xl:ml-[120px] xl:w-[280px] flex flex-col'>
                  <Button fullWidth={true} type="green" height="lg" fontSize={18} textCenter={true} href={useQuery('/checkout/accountability-coaching')} disableArrow={true} chevron={false} maxWidth={sm ? 280 : 0}>Sign Up Now</Button>
                </div>
              </div>
            </div>
          </RoundedContainer>
          <div className='xl:py-[140px] py-[80px]'>
            <Container>
              <div className='max-w-[600px] mx-auto mb-[60px]'>
                <H2>Membership & Pricing</H2>
              </div>
              <PanelsArray className='xl:mb-[100px] mb-[80px]' style='light' height='xl:h-[300px]' items={membershipItems} />
              <div className='flex scale-[0.9] mb-[32px] justify-center'>
                <CustomTrustpilot stars={true} large={true} vertical={false} changePosition={true}></CustomTrustpilot>
              </div>
              <div className='flex flex-col xl:flex-row xl:gap-[24px]'>
                <TestimonialBig
                  name="Yoav Koni"
                  className="testimonial-hero-anim-less drop-shadow-xl"
                  text="<span class='bg-[#FFC43A]'>Working with Summer at Commit Action has completely changed my life.</span> She has shown the perfect balance between keeping me focused and being flexible enough to help me become the person I want to be and achieve my biggest..."
                  avatarUrl={yoav_ava.src}
                  caption="Real Estate Developer"
                  height={270}
                ></TestimonialBig>
                <TestimonialBig
                  name="Mark Jayne"
                  className="testimonial-hero-anim-less drop-shadow-xl"
                  text="Commit Action has helped me gain clarity and productivity from day one and is definitely the <span class='bg-[#FFC43A]'>highest ROI investment I’ve ever made.</span> As a solo entrepreneur it’s very hard to keep everything together and they help me tremendously."
                  avatarUrl={mark_ava.src}
                  caption="CEO | Jayne Capital"
                  height={270}
                ></TestimonialBig>
                <TestimonialBig
                  name="Stephane L."
                  className="testimonial-hero-anim-less drop-shadow-xl"
                  text="I consider Commit Action to be my secret weapon enabling me to leap ahead on my purposeful and driven journey as an entrepreneur (for over 20 years now). <span class='bg-[#FFC43A]'>The ROI for the Commit Action service is over 10x in my opinion.</span>"
                  avatarUrl={stephane_ava.src}
                  caption="President | WebTarget Inc."
                  height={270}
                ></TestimonialBig>
              </div>
            </Container>
          </div>
          <div className='xl:py-[140px] py-[80px] bg-white'>
            <Container>
              <div className='xl:mb-[60px]'>
                <H2>Why Start Now?</H2>
              </div>
              <div className='flex flex-col xl:flex-row'>
                <div className='xl:w-[50%] mb-[60px] xl:mb-0'>
                  <div className='flex'>
                    <div className='max-w-[500px] text-[18px] text-black/70'>
                      <P className='mb-[16px]'>So many entrepreneurs just put up with being stressed out, inconsistent, and feeling guilty about not making progress on their biggest goals. But it doesn’t have to be like that.</P>
                      <P className='mb-[16px]'>We offer a 30-day no questions asked guarantee so you can try it risk free today.</P>
                      <P className='mb-[60px]'>Don’t be like so many of our clients who say that they regret not signing up sooner…</P>
                      <P className='mb-[16px] opacity-[1]'>Don’t let this be you. We’re here to help <span className='font-bold'>right now!</span></P>
                      <P className='font-bold opacity-[1]'>Are you ready to take action and grow your business faster and easier than ever before?</P>
                      <div className='lg:w-[280px] flex flex-col mt-[40px]'>
                        <Button fullWidth={true} type="green" height="lg" fontSize={18} textCenter={true} href={useQuery('/checkout/accountability-coaching')} disableArrow={true} chevron={false} maxWidth={sm ? 280 : 0}>Sign Up Now</Button>
                      </div>
                    </div>
                  </div>
                </div>
                <div className='w-full max-w-[590px] p-[20px] py-[40px] xl:p-[50px] xl:pb-[80px] bg-gray-100 rounded-[30px]'>
                  <div id="why-start-now-carousel">
                    <SimpleCarousel
                      loop={true}
                      width={whyStartNowWidth - 32}
                      autoplay={false}
                      doubleGap={16}
                      controlsVisible={false}
                      endLess={true}
                      bullets={true}
                      bulletsClass={'bullets--dark'}
                      overflow={'hidden'}
                      containerClass="checkout-testimonials-carousel2"
                      nextHtml={<span className='carousel_3d_next_control--sec carousel_quiz_next_control'></span>}
                      prevHtml={<span className='carousel_3d_prev_control--sec carousel_quiz_prev_control'></span>}
                    >
                      <SimpleCarouselItem>
                        <div>
                          <P className='mb-[40px]'><span className='font-bold text-black'>Sean</span> overshot his sales target by 251% in just his first month working with a coach, and wished he’d signed up sooner...</P>
                          <TestimonialBig
                            name="Sean C."
                            className="testimonial-hero-anim-less drop-shadow-xl"
                            text="Commit Action works exactly as described. My Coach Ryan has been a huge help, in a way that is practical, easy and results-driven. This month (which isn’t over yet) I am already at 251% of my monthly sales goal. I only wish I had contacted Commit Action sooner!! Highest possible recommendation!"
                            avatarUrl={sean_ava.src}
                            caption="Founder | Collins Media Group"
                          ></TestimonialBig>
                        </div>
                      </SimpleCarouselItem>
                      <SimpleCarouselItem>
                        <div>
                          <P className='mb-[40px]'><span className='font-bold text-black'>Danielle</span> had seen “millions” of our ads, but dragged her feet on trying it out. Once she got a coach, everything changed for her...</P>
                          <TestimonialBig
                            name="Danielle Dirks, Ph.D."
                            className="testimonial-hero-anim-less drop-shadow-xl"
                            text="Commit Action, and my coach in particular, Brittany, have been nothing short of life changing this past year of working with them. So many of my peers ask how I’m managing to get so much done and at scale, and I always point them back to having a supportive, trusted confidant who challenges me and redirects me to my highest priorities."
                            avatarUrl={danielle_ava.src}
                            caption="Founder | December December"
                          ></TestimonialBig>
                        </div>
                      </SimpleCarouselItem>
                      <SimpleCarouselItem>
                        <div>
                          <P className='mb-[40px]'><span className='font-bold text-black'>Julie</span> saw us again and again on Instagram, but it was only *another* missed deadline that finally convinced her to take the plunge...</P>
                          <TestimonialBig
                            name="Julie"
                            className="testimonial-hero-anim-less drop-shadow-xl"
                            text="I kept getting ads for Commit Action on instagram as I scrolled, and I finally decided to take a look in a fit of frustration over yet another missed personal deadline. One of the best decisions I've ever made."
                            avatarUrl={julie_ava.src}
                            caption="Freelance Writer & Editor"
                          ></TestimonialBig>
                        </div>
                      </SimpleCarouselItem>
                      <SimpleCarouselItem>
                        <div>
                          <P className='mb-[40px]'><span className='font-bold text-black'>Nitzan</span> gave us a try for just one month, but stayed after hitting his 1 year goal in just the first 30 days working with an accountability coach...</P>
                          <TestimonialBig
                            name="Nizan P."
                            className="testimonial-hero-anim-less drop-shadow-xl"
                            text="I saw Commit Action Instagram ad, and although I don't usually trust services and ads, I thought I'd give it a try for one month. Amazingly, with the guidance of Kelsey - my Commit action mentor, in this 1 month I've managed to achieve my 1 year goal.Commit action is simple, yet a very powerful tool, and I'm glad I gave it a chance, as it showed me that I'm capable"
                            avatarUrl={nitzan_ava.src}
                            caption="Founder | Long Story Shor"
                          ></TestimonialBig>
                        </div>
                      </SimpleCarouselItem>
                    </SimpleCarousel>
                  </div>
                </div>
              </div>
            </Container>
          </div>
          <div className='xl:pt-[140px] pt-[80px]'>
            <div className='container container-1200'>
              <div className='xl:mb-[60px]'>
                <H2>FAQ</H2>
              </div>
              <Accordion accordion_items={accountabilityCoachingFAQs}/>
            </div>
          </div>
        </RoundedContainer>
      </div>
    </>
  )
}

// @ts-ignore
AccountabilityCoach.getLayout = function getLayout(page: ReactNode) {
  return (
    <FatFooterLayout>
      {page}
    </FatFooterLayout>
  )
}

export default AccountabilityCoach
